// WidgetDashboard.jsx
// Install: npm install react-grid-layout recharts
// (MUI + emotion assumed installed)

import React, { useState, useCallback, useRef, useEffect } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  Box, Fab, Tooltip, IconButton, Typography, Paper, Chip,
  Drawer, Divider, Switch, Badge, Avatar,
} from "@mui/material";
import { alpha, createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Dashboard as DashboardIcon, Close as CloseIcon,
  DragIndicator as DragIcon, People as PeopleIcon,
  SwapHoriz as TxIcon, AttachMoney as RevenueIcon,
  Speed as UptimeIcon, AccountBalance as LoanIcon,
  CheckCircle as ApprovalIcon, Warning as DefaultIcon,
  TrendingUp as GrowthIcon, BarChart as BarChartIcon,
  ShowChart as LineChartIcon, DonutLarge as DonutIcon,
  MonitorHeart as HealthIcon, LightMode as LightIcon,
  DarkMode as DarkIcon, Delete as DeleteIcon, GridView as GridIcon,
} from "@mui/icons-material";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer, Legend,
} from "recharts";

// Static Data
const userTrendData = [
  { date: "May 20", total: 2100000, new: 580000 },
  { date: "May 21", total: 2180000, new: 620000 },
  { date: "May 22", total: 2230000, new: 710000 },
  { date: "May 23", total: 2300000, new: 780000 },
  { date: "May 24", total: 2370000, new: 820000 },
  { date: "May 25", total: 2420000, new: 840000 },
  { date: "May 26", total: 2450000, new: 854300 },
];
const txVolumeData = [
  { date: "May 20", volume: 95 }, { date: "May 21", volume: 108 },
  { date: "May 22", volume: 102 }, { date: "May 23", volume: 120 },
  { date: "May 24", volume: 145 }, { date: "May 25", volume: 130 },
  { date: "May 26", volume: 128 },
];
const revenueTrendData = [
  { date: "May 20", revenue: 0.9 }, { date: "May 21", revenue: 1.0 },
  { date: "May 22", revenue: 1.05 }, { date: "May 23", revenue: 1.1 },
  { date: "May 24", revenue: 1.15 }, { date: "May 25", revenue: 1.22 },
  { date: "May 26", revenue: 1.28 },
];
const defaultRateData = [
  { date: "May 20", rate: 0.065 }, { date: "May 21", rate: 0.058 },
  { date: "May 22", rate: 0.052 }, { date: "May 23", rate: 0.048 },
  { date: "May 24", rate: 0.040 }, { date: "May 25", rate: 0.035 },
  { date: "May 26", rate: 0.030 },
];
const loanPortfolioData = [
  { name: "Low Risk",    value: 62.4, color: "#4ade80" },
  { name: "Medium Risk", value: 29.6, color: "#facc15" },
  { name: "High Risk",   value: 8.0,  color: "#f87171" },
];
const crossSubsidiaryData = [
  { name: "Insurance",    value: 820.4 },
  { name: "Microfinance", value: 45.72 },
  { name: "Asset Mgmt",   value: 12.35 },
  { name: "Payments",     value: 128.45 },
];
const systemHealthItems = [
  { label: "All Systems", status: "Operational", color: "#4ade80" },
  { label: "Databases",   status: "Healthy",     color: "#4ade80" },
  { label: "Services",    status: "Running",     color: "#4ade80" },
  { label: "Security",    status: "Secure",      color: "#4ade80" },
];

// Widget Catalog
const WIDGET_CATALOG = [
  { id: "kpi-users",           label: "Active Users KPI",         icon: <PeopleIcon />,    category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-tx",              label: "Transaction Volume KPI",   icon: <TxIcon />,        category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-revenue",         label: "Revenue KPI",              icon: <RevenueIcon />,   category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-uptime",          label: "System Uptime KPI",        icon: <UptimeIcon />,    category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-loan-vol",        label: "Loan Volume KPI",          icon: <LoanIcon />,      category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-approval",        label: "Loan Approval Rate",       icon: <ApprovalIcon />,  category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-default",         label: "Default Rate",             icon: <DefaultIcon />,   category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "kpi-growth",          label: "Customer Growth",          icon: <GrowthIcon />,    category: "KPI",    defaultW: 3, defaultH: 2 },
  { id: "chart-user-trend",    label: "User Growth Trend",        icon: <LineChartIcon />, category: "Charts", defaultW: 8, defaultH: 5 },
  { id: "chart-tx-trend",      label: "Transaction Volume Trend", icon: <BarChartIcon />,  category: "Charts", defaultW: 6, defaultH: 5 },
  { id: "chart-rev-trend",     label: "Revenue Trend",            icon: <LineChartIcon />, category: "Charts", defaultW: 6, defaultH: 5 },
  { id: "chart-default-rate",  label: "Default Rate Trend",       icon: <LineChartIcon />, category: "Charts", defaultW: 8, defaultH: 5 },
  { id: "chart-loan-portfolio",label: "Loan Portfolio Donut",     icon: <DonutIcon />,     category: "Charts", defaultW: 5, defaultH: 5 },
  { id: "chart-subsidiary",    label: "Cross-Subsidiary Perf.",   icon: <BarChartIcon />,  category: "Charts", defaultW: 7, defaultH: 5 },
  { id: "system-health",       label: "System Health Status",     icon: <HealthIcon />,    category: "System", defaultW: 4, defaultH: 5 },
];

const KPI_DATA = {
  "kpi-users":    { label: "Total Active Users",  value: "2.45M",    trend: 12.6, sub: "vs May 13–19", icon: <PeopleIcon />,   color: "#6366f1" },
  "kpi-tx":       { label: "Transaction Volume",  value: "₦128.45B", trend: 18.3, sub: "vs May 13–19", icon: <TxIcon />,       color: "#06b6d4" },
  "kpi-revenue":  { label: "Revenue (Fees)",      value: "₦1.28B",   trend: 15.7, sub: "vs May 13–19", icon: <RevenueIcon />,  color: "#4ade80" },
  "kpi-uptime":   { label: "System Uptime",       value: "99.98%",   trend: 0.02, sub: "vs May 13–19", icon: <UptimeIcon />,   color: "#f59e0b" },
  "kpi-loan-vol": { label: "Loan Volume",         value: "₦45.72B",  trend: 21.4, sub: "vs May 13–19", icon: <LoanIcon />,     color: "#a855f7" },
  "kpi-approval": { label: "Loan Approval Rate",  value: "87.6%",    trend: 3.4,  sub: "vs May 13–19", icon: <ApprovalIcon />, color: "#4ade80" },
  "kpi-default":  { label: "Default Rate",        value: "0.03%",    trend: -95,  sub: "vs May 13–19", icon: <DefaultIcon />,  color: "#f87171" },
  "kpi-growth":   { label: "Customer Growth",     value: "+18.7%",   trend: 18.7, sub: "2.45M Total",  icon: <GrowthIcon />,   color: "#06b6d4" },
};

const DROP_POSITIONS = [
  { label: "Full Row",    hint: "100%", cols: 12, offset: 0 },
  { label: "Left Half",  hint: "50%",  cols: 6,  offset: 0 },
  { label: "Right Half", hint: "50%",  cols: 6,  offset: 6 },
  { label: "Left Third", hint: "33%",  cols: 4,  offset: 0 },
  { label: "Mid Third",  hint: "33%",  cols: 4,  offset: 4 },
  { label: "Rgt Third",  hint: "33%",  cols: 4,  offset: 8 },
];

const DEFAULT_LAYOUT = [
  { i: "kpi-users",            x: 0,  y: 0,  w: 3, h: 2 },
  { i: "kpi-tx",               x: 3,  y: 0,  w: 3, h: 2 },
  { i: "kpi-revenue",          x: 6,  y: 0,  w: 3, h: 2 },
  { i: "kpi-uptime",           x: 9,  y: 0,  w: 3, h: 2 },
  { i: "kpi-loan-vol",         x: 0,  y: 2,  w: 3, h: 2 },
  { i: "kpi-approval",         x: 3,  y: 2,  w: 3, h: 2 },
  { i: "kpi-default",          x: 6,  y: 2,  w: 3, h: 2 },
  { i: "kpi-growth",           x: 9,  y: 2,  w: 3, h: 2 },
  { i: "chart-user-trend",     x: 0,  y: 4,  w: 8, h: 5 },
  { i: "system-health",        x: 8,  y: 4,  w: 4, h: 5 },
  { i: "chart-tx-trend",       x: 0,  y: 9,  w: 6, h: 5 },
  { i: "chart-rev-trend",      x: 6,  y: 9,  w: 6, h: 5 },
  { i: "chart-loan-portfolio", x: 0,  y: 14, w: 5, h: 5 },
  { i: "chart-subsidiary",     x: 5,  y: 14, w: 7, h: 5 },
];

// Trend Chip
function TrendChip({ value }) {
  const up = value >= 0;
  return (
    <Chip label={`${up ? "▲" : "▼"} ${Math.abs(value)}%`} size="small" sx={{
      height: 20, fontSize: 10, fontWeight: 700,
      bgcolor: up ? alpha("#4ade80", 0.15) : alpha("#f87171", 0.15),
      color: up ? "#4ade80" : "#f87171",
    }} />
  );
}

// KPI Card
function KpiCard({ label, value, trend, sub, icon, color }) {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", p: 2, gap: 2 }}>
      <Avatar sx={{ bgcolor: alpha(color, 0.15), color, width: 48, height: 48, flexShrink: 0 }}>{icon}</Avatar>
      <Box>
        <Typography variant="caption" sx={{ opacity: 0.55, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, my: 0.25 }}>{value}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <TrendChip value={trend} />
          <Typography variant="caption" sx={{ opacity: 0.4 }}>{sub}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// Chart wrapper
function CW({ title, children, footer }) {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, flexShrink: 0 }}>{title}</Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
      {footer && <Box sx={{ mt: 1, flexShrink: 0 }}>{footer}</Box>}
    </Box>
  );
}

function UserGrowthChart({ dark }) {
  const s = dark ? "#94a3b8" : "#64748b";
  return <CW title="User Growth Trend" footer={
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box><Typography variant="caption" sx={{ opacity: 0.5 }}>New Users</Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>85,430</Typography><TrendChip value={16.5} /></Box>
      </Box>
      <Box><Typography variant="caption" sx={{ opacity: 0.5 }}>Inactive Users</Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>341,721</Typography><TrendChip value={-4.1} /></Box>
      </Box>
    </Box>
  }>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={userTrendData}>
        <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e293b" : "#e2e8f0"} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: s }} />
        <YAxis tickFormatter={v => `${(v/1e6).toFixed(1)}M`} tick={{ fontSize: 10, fill: s }} width={42} />
        <RechartTooltip contentStyle={{ background: dark ? "#0f172a" : "#fff", border: "none", borderRadius: 8, fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="total" name="Total Users" stroke="#6366f1" strokeWidth={2.5} dot={false} />
        <Line type="monotone" dataKey="new"   name="New Users"   stroke="#4ade80" strokeWidth={2.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </CW>;
}

function TxVolumeChart({ dark }) {
  const s = dark ? "#94a3b8" : "#64748b";
  return <CW title="Transaction Volume Trend (₦)" footer={
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box><Typography variant="caption" sx={{ opacity: 0.5 }}>Avg. Daily</Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>₦21.41B</Typography><TrendChip value={17.8} /></Box>
      </Box>
      <Box><Typography variant="caption" sx={{ opacity: 0.5 }}>Peak Day</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>May 24 — ₦28.72B</Typography>
      </Box>
    </Box>
  }>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={txVolumeData}>
        <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e293b" : "#e2e8f0"} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: s }} />
        <YAxis tickFormatter={v => `₦${v}B`} tick={{ fontSize: 10, fill: s }} width={45} />
        <RechartTooltip contentStyle={{ background: dark ? "#0f172a" : "#fff", border: "none", borderRadius: 8, fontSize: 12 }} formatter={v => [`₦${v}B`, "Volume"]} />
        <Bar dataKey="volume" fill="#6366f1" radius={[4,4,0,0]} />
      </BarChart>
    </ResponsiveContainer>
  </CW>;
}

function RevenueTrendChart({ dark }) {
  const s = dark ? "#94a3b8" : "#64748b";
  return <CW title="Revenue Trend (₦)" footer={
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box><Typography variant="caption" sx={{ opacity: 0.5 }}>Total Revenue</Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>₦1.28B</Typography><TrendChip value={15.7} /></Box>
      </Box>
      <Box><Typography variant="caption" sx={{ opacity: 0.5 }}>Avg. Daily</Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>₦182.86M</Typography><TrendChip value={13.2} /></Box>
      </Box>
    </Box>
  }>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={revenueTrendData}>
        <defs>
          <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#a855f7" stopOpacity={0.35} />
            <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e293b" : "#e2e8f0"} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: s }} />
        <YAxis tickFormatter={v => `₦${v}B`} tick={{ fontSize: 10, fill: s }} width={45} />
        <RechartTooltip contentStyle={{ background: dark ? "#0f172a" : "#fff", border: "none", borderRadius: 8, fontSize: 12 }} formatter={v => [`₦${v}B`, "Revenue"]} />
        <Area type="monotone" dataKey="revenue" stroke="#a855f7" fill="url(#rg)" strokeWidth={2.5} />
      </AreaChart>
    </ResponsiveContainer>
  </CW>;
}

function DefaultRateChart({ dark }) {
  const s = dark ? "#94a3b8" : "#64748b";
  return <CW title="Default Rate Trend" footer={
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="caption" sx={{ opacity: 0.5 }}>Current:</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>0.03%</Typography>
      <TrendChip value={-95} />
    </Box>
  }>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={defaultRateData}>
        <defs>
          <linearGradient id="dg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#4ade80" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e293b" : "#e2e8f0"} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: s }} />
        <YAxis tickFormatter={v => `${(v*100).toFixed(1)}%`} tick={{ fontSize: 10, fill: s }} width={38} />
        <RechartTooltip contentStyle={{ background: dark ? "#0f172a" : "#fff", border: "none", borderRadius: 8, fontSize: 12 }} formatter={v => [`${(v*100).toFixed(2)}%`, "Default Rate"]} />
        <Area type="monotone" dataKey="rate" stroke="#4ade80" fill="url(#dg)" strokeWidth={2.5} />
      </AreaChart>
    </ResponsiveContainer>
  </CW>;
}

function LoanPortfolioChart({ dark }) {
  return <CW title="Loan Portfolio Overview">
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 2 }}>
      <Box sx={{ width: "55%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={loanPortfolioData} cx="50%" cy="50%" innerRadius="55%" outerRadius="80%" dataKey="value" paddingAngle={3}>
              {loanPortfolioData.map((e, i) => <Cell key={i} fill={e.color} />)}
            </Pie>
            <RechartTooltip contentStyle={{ background: dark ? "#0f172a" : "#fff", border: "none", borderRadius: 8, fontSize: 12 }} formatter={v => [`${v}%`]} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>₦45.72B</Typography>
        <Typography variant="caption" sx={{ opacity: 0.45 }}>Total Portfolio</Typography>
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          {loanPortfolioData.map(d => (
            <Box key={d.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ flex: 1, opacity: 0.65 }}>{d.name}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{d.value}%</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </CW>;
}

function CrossSubsidiaryWidget({ dark }) {
  const s = dark ? "#94a3b8" : "#64748b";
  const cols = ["#6366f1","#4ade80","#f59e0b","#06b6d4"];
  return <CW title="Cross-Subsidiary Performance">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={crossSubsidiaryData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e293b" : "#e2e8f0"} horizontal={false} />
        <XAxis type="number" tickFormatter={v => `₦${v}B`} tick={{ fontSize: 10, fill: s }} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: s }} width={85} />
        <RechartTooltip contentStyle={{ background: dark ? "#0f172a" : "#fff", border: "none", borderRadius: 8, fontSize: 12 }} formatter={v => [`₦${v}B`]} />
        <Bar dataKey="value" radius={[0,4,4,0]}>
          {crossSubsidiaryData.map((_,i) => <Cell key={i} fill={cols[i%cols.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </CW>;
}

function SystemHealthWidget() {
  return <CW title="System Health Status">
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, height: "100%", alignContent: "start" }}>
      {systemHealthItems.map(item => (
        <Paper key={item.label} elevation={0} sx={{
          p: 1.5, borderRadius: 2, textAlign: "center",
          border: `1px solid ${alpha(item.color, 0.25)}`,
          bgcolor: alpha(item.color, 0.06),
        }}>
          <Box sx={{ width: 38, height: 38, borderRadius: "50%", border: `2.5px solid ${item.color}`, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: item.color }} />
          </Box>
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, fontSize: 11 }}>{item.label}</Typography>
          <Typography variant="caption" sx={{ color: item.color, fontWeight: 600, fontSize: 10 }}>{item.status}</Typography>
        </Paper>
      ))}
    </Box>
  </CW>;
}

function renderWidget(id, dark) {
  if (KPI_DATA[id]) return <KpiCard {...KPI_DATA[id]} />;
  switch (id) {
    case "chart-user-trend":     return <UserGrowthChart dark={dark} />;
    case "chart-tx-trend":       return <TxVolumeChart dark={dark} />;
    case "chart-rev-trend":      return <RevenueTrendChart dark={dark} />;
    case "chart-default-rate":   return <DefaultRateChart dark={dark} />;
    case "chart-loan-portfolio": return <LoanPortfolioChart dark={dark} />;
    case "chart-subsidiary":     return <CrossSubsidiaryWidget dark={dark} />;
    case "system-health":        return <SystemHealthWidget />;
    default: return <Box sx={{ p: 2 }}><Typography>{id}</Typography></Box>;
  }
}

// Drop Zone Bar
function DropZoneBar({ onDrop, dark }) {
  const [active, setActive] = useState(null);
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: `2px dashed ${dark ? alpha("#6366f1",0.4) : alpha("#6366f1",0.3)}`, bgcolor: dark ? alpha("#6366f1",0.04) : alpha("#6366f1",0.02) }}>
      <Typography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>↓ Drop widget onto a zone</Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {DROP_POSITIONS.map(pos => (
          <Box key={pos.label}
            onDragOver={e => { e.preventDefault(); setActive(pos.label); }}
            onDragLeave={() => setActive(null)}
            onDrop={e => { e.preventDefault(); setActive(null); const id = e.dataTransfer.getData("widgetId"); if (id) onDrop(id, pos); }}
            sx={{
              flex: 1, height: 52, borderRadius: 1.5, cursor: "copy",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              border: `1.5px solid ${active === pos.label ? "#6366f1" : dark ? alpha("#fff",0.12) : alpha("#000",0.1)}`,
              bgcolor: active === pos.label ? alpha("#6366f1",0.15) : "transparent",
              transition: "all 0.15s",
              "&:hover": { borderColor: "#6366f1", bgcolor: alpha("#6366f1",0.08) },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, lineHeight: 1.2 }}>{pos.label}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.4, fontSize: 9 }}>{pos.hint}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Widget Palette Drawer
function WidgetPalette({ open, onClose, dark, activeWidgets }) {
  const cats = [...new Set(WIDGET_CATALOG.map(w => w.category))];
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 288, bgcolor: dark ? "#0d1526" : "#f8fafc", borderLeft: `1px solid ${dark ? alpha("#fff",0.07) : alpha("#000",0.07)}` } }}
    >
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GridIcon sx={{ color: "#6366f1", fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Widget Library</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.45, display: "block", mb: 2, lineHeight: 1.5 }}>
          Drag a widget → drop onto a zone in the dashboard
        </Typography>
        <Divider sx={{ mb: 2.5 }} />
        {cats.map(cat => (
          <Box key={cat} sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.35, mb: 1, display: "block", fontSize: 10 }}>{cat}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {WIDGET_CATALOG.filter(w => w.category === cat).map(w => {
                const onBoard = activeWidgets.includes(w.id);
                return (
                  <Box key={w.id} draggable={!onBoard}
                    onDragStart={e => { if (onBoard) { e.preventDefault(); return; } e.dataTransfer.setData("widgetId", w.id); e.dataTransfer.effectAllowed = "copy"; }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5, p: 1.25, borderRadius: 1.5, userSelect: "none",
                      cursor: onBoard ? "not-allowed" : "grab", opacity: onBoard ? 0.45 : 1,
                      border: `1px solid ${dark ? alpha("#fff",0.06) : alpha("#000",0.06)}`,
                      bgcolor: dark ? alpha("#fff",0.02) : "#fff",
                      transition: "all 0.15s",
                      ...(!onBoard && { "&:hover": { borderColor: "#6366f1", bgcolor: alpha("#6366f1",0.07), transform: "translateX(-2px)" } }),
                    }}
                  >
                    <Box sx={{ color: onBoard ? "text.disabled" : "#6366f1", display: "flex", flexShrink: 0 }}>{w.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, fontSize: 13 }}>{w.label}</Typography>
                    {onBoard
                      ? <Chip label="Added" size="small" sx={{ height: 16, fontSize: 9, bgcolor: alpha("#4ade80",0.12), color: "#4ade80" }} />
                      : <DragIcon sx={{ opacity: 0.25, fontSize: 15 }} />}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}

// Grid Widget Shell
function GridWidgetShell({ widgetId, onRemove, dark }) {
  return (
    <Box sx={{ height: "100%", position: "relative", "&:hover .rbtn": { opacity: 1 } }}>
      <Box className="drag-handle" sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 20, zIndex: 10, cursor: "move", borderRadius: "12px 12px 0 0", "&:hover": { background: alpha("#6366f1",0.1) } }} />
      <Paper elevation={0} sx={{
        height: "100%", borderRadius: 2, overflow: "hidden",
        border: `1px solid ${dark ? alpha("#fff",0.07) : alpha("#000",0.07)}`,
        bgcolor: dark ? "#1e293b" : "#fff",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: dark ? "0 4px 32px rgba(0,0,0,0.45)" : "0 4px 24px rgba(0,0,0,0.09)" },
      }}>
        {renderWidget(widgetId, dark)}
      </Paper>
      <IconButton className="rbtn" size="small" onClick={() => onRemove(widgetId)} sx={{
        position: "absolute", top: 6, right: 6, opacity: 0, transition: "opacity 0.2s", zIndex: 20,
        bgcolor: alpha("#f87171",0.12), color: "#f87171", width: 24, height: 24,
        "&:hover": { bgcolor: alpha("#f87171",0.25) },
      }}>
        <DeleteIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  );
}

// Main Component
export default function WidgetDashboard() {
  const [darkMode, setDarkMode] = useState(true);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [activeWidgets, setActiveWidgets] = useState(DEFAULT_LAYOUT.map(l => l.i));
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const update = () => { if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth); };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: { main: "#6366f1" },
      background: { default: darkMode ? "#080e1e" : "#f1f5f9", paper: darkMode ? "#1e293b" : "#ffffff" },
    },
    typography: { fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" },
    shape: { borderRadius: 12 },
  });

  const handleDrop = useCallback((widgetId, pos) => {
    if (activeWidgets.includes(widgetId)) return;
    const cat = WIDGET_CATALOG.find(w => w.id === widgetId);
    const maxY = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    setLayout(prev => [...prev, { i: widgetId, x: pos.offset, y: maxY, w: pos.cols, h: cat?.defaultH ?? 4 }]);
    setActiveWidgets(prev => [...prev, widgetId]);
  }, [activeWidgets, layout]);

  const handleRemove = useCallback((widgetId) => {
    setLayout(prev => prev.filter(l => l.i !== widgetId));
    setActiveWidgets(prev => prev.filter(id => id !== widgetId));
  }, []);

  return (
    <Box className="page-container">
        <ThemeProvider theme={theme}>
        <Box ref={containerRef} sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary", p: { xs: 1.5, md: 2.5 }, transition: "background 0.3s, color 0.3s" }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1 }}>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>Executive Summary Dashboard</Typography>
                <Typography variant="caption" sx={{ opacity: 0.45 }}>May 20 – May 26, 2025 · Last updated: 5m ago · All times in WAT</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: darkMode ? alpha("#fff",0.05) : alpha("#000",0.05), borderRadius: 3, px: 1.25, py: 0.5 }}>
                <LightIcon sx={{ fontSize: 16, color: "#f59e0b", opacity: darkMode ? 0.4 : 1 }} />
                <Switch size="small" checked={darkMode} onChange={e => setDarkMode(e.target.checked)} sx={{ "& .MuiSwitch-thumb": { bgcolor: "#6366f1" } }} />
                <DarkIcon sx={{ fontSize: 16, color: "#6366f1", opacity: darkMode ? 1 : 0.4 }} />
            </Box>
            </Box>

            {/* Drop Zone */}
            <DropZoneBar onDrop={handleDrop} dark={darkMode} />

            {/* Grid */}
            {activeWidgets.length > 0 ? (
            <GridLayout layout={layout} cols={12} rowHeight={50} width={containerWidth}
                onLayoutChange={l => setLayout(l)} draggableHandle=".drag-handle"
                margin={[12, 12]} containerPadding={[0, 0]} resizeHandles={["se","sw","ne","nw"]} compactType="vertical">
                {activeWidgets.map(id => (
                <Box key={id}>
                    <GridWidgetShell widgetId={id} onRemove={handleRemove} dark={darkMode} />
                </Box>
                ))}
            </GridLayout>
            ) : (
            <Box sx={{ mt: 10, textAlign: "center", opacity: 0.35, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <DashboardIcon sx={{ fontSize: 72 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Your dashboard is empty</Typography>
                <Typography variant="body2">Click the purple button (bottom-right) → drag a widget → drop onto a zone above</Typography>
            </Box>
            )}

            {/* FAB */}
            <Tooltip title={`Widget Library (${WIDGET_CATALOG.length - activeWidgets.length} available)`} placement="left">
            <Fab onClick={() => setPaletteOpen(true)} sx={{
                position: "fixed", bottom: 32, right: 32, zIndex: 1300,
                bgcolor: "#6366f1", color: "#fff",
                boxShadow: "0 6px 32px rgba(99,102,241,0.55)",
                "&:hover": { bgcolor: "#4f46e5", transform: "scale(1.06)" },
                transition: "all 0.2s",
            }}>
                <Badge badgeContent={WIDGET_CATALOG.length - activeWidgets.length} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 18, height: 18 } }}>
                <DashboardIcon />
                </Badge>
            </Fab>
            </Tooltip>

            {/* Palette Drawer */}
            <WidgetPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} dark={darkMode} activeWidgets={activeWidgets} />
        </Box>
        </ThemeProvider>
    </Box>
  );
}